/**
 * Client AOI subscription query builders.
 *
 * Mirror of `server/src/subscriptions/*`. Column names are snake_case storage
 * form (the schema is camelCase, converted by the default SnakeCase policy).
 *
 * PER-CELL EQUALITY, not a range: SpacetimeDB evaluates indexed EQUALITY
 * predicates via the index, while inequality predicates (`>= AND <=`) force
 * full-table processing per query (spacetimedb.com/docs/tables/performance).
 * Identical query strings are also deduplicated server-side, which makes
 * per-cell queries the recommended AOI primitive: when the window shifts,
 * the overlapping cells' queries are byte-identical between the old and new
 * subscription sets and cost nothing to re-subscribe. A radius-2 window is
 * 25 small indexed queries per table instead of 1 full-scan range query.
 */

import type { CellRange } from '../config.js';

/** One equality query per cell in the window (inclusive), for `table`. */
function perCellSql(table: string, worldId: bigint, r: CellRange): string[] {
  const out: string[] = [];
  for (let x = r.minX; x <= r.maxX; x++) {
    for (let z = r.minZ; z <= r.maxZ; z++) {
      out.push(`SELECT * FROM ${table} WHERE world_id = ${worldId} AND cell_x = ${x} AND cell_z = ${z}`);
    }
  }
  return out;
}

/** Players in `worldId` within an inclusive AOI cell window (one query per cell). */
export function nearbyPlayersSql(worldId: bigint, r: CellRange): string[] {
  return perCellSql('player_state', worldId, r);
}

/** Objects in `worldId` within an inclusive AOI cell window (one query per cell). */
export function worldObjectsSql(worldId: bigint, r: CellRange): string[] {
  return perCellSql('world_object', worldId, r);
}

/** Versona hot state for the CURRENT world (indexed by world_id; 243 NPCs total, so a
 *  per-world window is plenty — per-cell NPC AOI returns behind a proper migration when
 *  NPC counts warrant it: adding cell columns to the populated table needs default
 *  annotations the SDK doesn't emit for numerics, and mid-table columns reorder). */
/** The route-ahead rows (versona_route, 2026-09-15) in the SAME position box as the hot rows — (x0, z0) IS the hot row. */
export function versonaRouteSql(worldId: bigint, cellX: number, cellZ: number): string[] {
  const cx = (cellX + 0.5) * 64;
  const cz = (cellZ + 0.5) * 64;
  const R = 560;
  return [
    `SELECT * FROM versona_route WHERE world_id = ${worldId}`
    + ` AND x0 >= ${Math.floor(cx - R)} AND x0 <= ${Math.ceil(cx + R)}`
    + ` AND z0 >= ${Math.floor(cz - R)} AND z0 <= ${Math.ceil(cz + R)}`,
  ];
}

export function versonaStateSql(worldId: bigint, cellX: number, cellZ: number): string[] {
  // POSITION BOX, not per-world (town-life wave): 2,066 residents with
  // cross-town commuters changed ~1,400 rows / 2 s — replicating the whole
  // world's hot table starved the client (the join-freeze storm class). The
  // box rides the AOI recenter (±560 m covers the 400 m render cull + the
  // hysteresis drift); the range filter walks the world_id index server-side
  // (2 k rows — trivial), and hot-row presence == in-window is exactly the
  // AOI semantic EnvironmentModule was built on.
  const cx = (cellX + 0.5) * 64;
  const cz = (cellZ + 0.5) * 64;
  const R = 560;
  return [
    `SELECT * FROM versona_state WHERE world_id = ${worldId}`
    + ` AND cur_x >= ${Math.floor(cx - R)} AND cur_x <= ${Math.ceil(cx + R)}`
    + ` AND cur_z >= ${Math.floor(cz - R)} AND cur_z <= ${Math.ceil(cz + R)}`,
  ];
}
